// ----- phase-movable-dialogs — THE shared movable dialog shell -----
//
// Sharon's obstruction (2026-08-02): a dialog covers the very content you
// need while using it. The fix is app-wide, through ONE behaviour: this
// shell composes the two existing evaluators —
//
//   * `useDraggableModal`  (phase-ui-draggable-modals →
//     phase-draggable-resizable-editor-modal): title-bar drag, optional
//     bottom-right resize, clamped never-fully-off-screen geometry
//     (MIN_VISIBLE_PX), small-viewport fallback, double-click-title reset,
//     and geometry persistence under `autoectd.modalGeometry.<persistKey>`.
//   * `useModalA11y`: Escape-to-close + focus trap.
//
// NO second drag/resize implementation exists — this file only arranges
// overlay + window + title bar around those hooks so that converting a
// bespoke modal is a handful of lines. A structural test
// (`movableDialogPinning.test.ts`) enumerates every dialog in the app and
// asserts it renders through this shell (or calls the hook directly, for
// the three pre-existing consumers) — a future dialog cannot quietly fork.
//
// Behaviour contract (the phase's requirements):
//   - Drag by the TITLE BAR only — text selection inside the body works.
//   - Position persists per dialog (persistKey) and is restored clamped
//     into the viewport; "within the session" is satisfied via the hook's
//     existing localStorage persistence (cross-session memory falls out
//     for free — the sanctioned free path).
//   - Escape closes (useModalA11y), unless `escDisabled` (busy guard).
//   - Never draggable fully off-screen (the hook's clampOffset).
//   - First open with no remembered geometry: centred, as today.
//   - Background stays NON-interactive: the overlay still eats clicks.
//     (Click-through is explicitly out of scope.)

import type { CSSProperties, ReactElement, ReactNode, MouseEvent } from 'react';
import {
  useDraggableModal,
  RESIZE_GRIP_STYLE,
  type SizeLike,
} from '@/hooks/useDraggableModal';
import { useModalA11y } from '@/hooks/useModalA11y';

export interface MovableDialogProps {
  /** Geometry memory key — one per dialog kind. */
  readonly persistKey: string;
  /** Title-bar content (string or richer node). The drag handle. */
  readonly title: ReactNode;
  /** Escape / backdrop close. Omit for dialogs that must not dismiss. */
  readonly onClose?: (() => void) | undefined;
  /** Suppress Escape while busy (mirrors ConfirmModal's guard). */
  readonly escDisabled?: boolean;
  /** Also close when the backdrop is clicked (legacy behaviour of some dialogs). */
  readonly closeOnBackdrop?: boolean;
  /** Show the bottom-right resize grip. */
  readonly resizable?: boolean;
  readonly minSize?: SizeLike;
  /** data-testid for the WINDOW element (the conventional per-dialog id). */
  readonly testId?: string;
  /** data-testid for the overlay, where a legacy test queries it. */
  readonly overlayTestId?: string;
  readonly ariaLabel?: string;
  /** id of the element that labels the dialog (aria-labelledby). */
  readonly ariaLabelledBy?: string;
  /** Default window width (CSS); the card keeps its own look. */
  readonly width?: CSSProperties['width'];
  readonly maxWidth?: CSSProperties['maxWidth'];
  readonly maxHeight?: CSSProperties['maxHeight'];
  /** Extra style on the window card (padding overrides etc.). */
  readonly windowStyle?: CSSProperties;
  /** Right-hand side of the title bar (✕ buttons, chips). */
  readonly headerRight?: ReactNode;
  /** Style override for the body wrapper (defaults to scrollable flex column). */
  readonly bodyStyle?: CSSProperties;
  readonly zIndex?: number;
  readonly children: ReactNode;
}

/**
 * The assistant FAB floats at z-index 900/901 in the app's bottom-right
 * corner — ABOVE the admin dialogs' historical z-indexes (300–350), so it
 * covered them and, once dialogs became resizable, swallowed the pointer
 * events of the bottom-right resize grip (found in a real browser during
 * this phase's render-and-look). Every dialog is lifted by this offset, which
 * preserves the callers' RELATIVE stacking exactly (300→1000, 320→1020,
 * 1100→1800) while putting the lowest of them above the FAB.
 */
const DIALOG_Z_OFFSET = 700;

const overlayBase: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardBase: CSSProperties = {
  // `relative` anchors the absolutely-positioned resize grip to the DIALOG.
  // Without it the grip escapes to the fixed overlay (i.e. the viewport
  // corner) and resizing silently does nothing.
  position: 'relative',
  background: 'var(--surface, #fff)',
  borderRadius: 8,
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '92vw',
  maxHeight: '88vh',
  minWidth: 0,
};

const titleBarBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px',
  borderBottom: '1px solid var(--border, #e5e7eb)',
  cursor: 'grab',
  userSelect: 'none',
  flexShrink: 0,
};

export function MovableDialog({
  persistKey,
  title,
  onClose,
  escDisabled = false,
  closeOnBackdrop = false,
  resizable = false,
  minSize,
  testId,
  overlayTestId,
  ariaLabel,
  ariaLabelledBy,
  width,
  maxWidth,
  maxHeight,
  windowStyle,
  headerRight,
  bodyStyle,
  zIndex = 1000,
  children,
}: MovableDialogProps): ReactElement {
  const drag = useDraggableModal({
    persistKey,
    ...(minSize !== undefined && { minSize }),
  });
  const a11y = useModalA11y({
    onClose: onClose ?? (() => undefined),
    disabled: escDisabled || !onClose,
  });

  const setRefs = (el: HTMLElement | null): void => {
    drag.windowRef(el);
    a11y.ref(el);
  };

  const onBackdropMouseDown = (e: MouseEvent<HTMLDivElement>): void => {
    if (closeOnBackdrop && onClose && e.target === e.currentTarget) onClose();
  };

  return (
    <div
      style={{ ...overlayBase, zIndex: zIndex + DIALOG_Z_OFFSET }}
      role="presentation"
      onMouseDown={onBackdropMouseDown}
      {...(overlayTestId !== undefined && { 'data-testid': overlayTestId })}
    >
      <div
        ref={setRefs}
        role="dialog"
        aria-modal="true"
        {...(ariaLabel !== undefined && { 'aria-label': ariaLabel })}
        {...(ariaLabelledBy !== undefined && { 'aria-labelledby': ariaLabelledBy })}
        tabIndex={a11y.tabIndex}
        onKeyDown={a11y.onKeyDown}
        {...(testId !== undefined && { 'data-testid': testId })}
        style={{
          ...cardBase,
          ...(width !== undefined && { width }),
          ...(maxWidth !== undefined && { maxWidth }),
          ...(maxHeight !== undefined && { maxHeight }),
          // Reserve a strip below the scrolling body for the resize grip.
          // Without it the body's vertical scrollbar sits over the grip and
          // swallows its pointer events (the browser hit-tests scrollbars
          // ahead of overlapping positioned siblings) — resizing silently
          // does nothing. Verified in a real browser, not just jsdom.
          ...(resizable ? { paddingBottom: 16 } : {}),
          ...windowStyle,
          ...drag.windowStyle,
        }}
      >
        <div
          {...drag.handleProps}
          data-testid="movable-dialog-titlebar"
          style={{
            ...titleBarBase,
            // The hook's own handle style carries touch-action:none — merge,
            // never replace, or dragging breaks on touch/pointer devices.
            ...drag.handleProps.style,
            cursor: drag.dragging ? 'grabbing' : 'grab',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 15 }}>{title}</div>
          {headerRight}
        </div>
        <div
          style={{
            padding: '14px 16px',
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            ...bodyStyle,
          }}
        >
          {children}
        </div>
        {resizable && !drag.smallViewport && (
          <div
            {...drag.resizeHandleProps}
            data-testid="movable-dialog-resize-grip"
            style={{
              ...RESIZE_GRIP_STYLE,
              ...drag.resizeHandleProps.style,
              // Sit in the reserved strip, clear of the body's scrollbar.
              bottom: 0,
              right: 0,
            }}
          />
        )}
      </div>
    </div>
  );
}
